import { DateTime, Duration, Settings, IANAZone } from 'luxon';
import { Injectable } from '@angular/core';

/**
 * DateMoment — Luxon-based date utility.
 * Method signatures preserved for backward compatibility.
 * Internally uses Luxon DateTime instead of Moment.
 */
@Injectable()
export class DateMoment {
    public static FORMAT = {
        WITH_HOUR: 'dd MMM yyyy HH:mm',
        WITHOUT_HOUR: 'dd MMM yyyy'
    };

    public static current(utc = false) {
        return utc ? DateTime.utc() : DateTime.now();
    }

    public static getToday() {
        return DateMoment.current().startOf('day');
    }

    public static createDateMoment(year: number, month = 1, date = 1, hour = 0, minute = 0, second = 0, millisecond = 0) {
        return DateTime.fromObject({ year, month, day: date, hour, minute, second, millisecond });
    }

    public static getOffSet(value: DateTime) {
        return value.offset / 60;
    }

    public static toDate(value: DateTime) {
        return value.toJSDate();
    }

    public static getCurrentDateAtTimezone(timezone: string) {
        return DateTime.now().setZone(timezone);
    }

    public static formatDate(dateString: any, format = 'dd MMM yyyy', isUTC = false) {
        const date = DateMoment.date(dateString, isUTC);
        if (date && DateTime.isDateTime(date) && date.isValid) {
            return date.toFormat(format);
        }
        return dateString;
    }

    public static dateDiff(startDate: any, endDate: any, isDateTimeObject = false, unit: string = 'milliseconds') {
        if (!isDateTimeObject) {
            startDate = DateMoment.date(startDate);
            endDate = DateMoment.date(endDate);
        }

        if (!DateTime.isDateTime(startDate) || !DateTime.isDateTime(endDate)) return 0;

        const diff = endDate.diff(startDate, unit as any);
        return diff.as(unit as any);
    }

    public static setDefaultTimezone(timezone?: string) {
        if (timezone) {
            Settings.defaultZone = timezone;
            return timezone;
        } else {
            const guessed = DateTime.now().zoneName;
            Settings.defaultZone = guessed;
            return guessed;
        }
    }

    public static getCurrentTimeZone() {
        return DateTime.now().zoneName;
    }

    public static getTime(value: DateTime, type: string) {
        switch (type) {
            case 'date':
                return value.day;
            case 'day':
                return value.weekday % 7; // Luxon weekday: 1=Mon..7=Sun → convert to 0=Sun..6=Sat
            case 'month':
                return value.month - 1; // Luxon months are 1-based, moment was 0-based
            case 'year':
                return value.year;
            default:
                break;
        }
    }

    public static date(value?: any, utc = false): DateTime | any {
        if (!value) return value;

        let dt: DateTime;

        if (DateTime.isDateTime(value)) {
            return value;
        }

        if (utc) {
            dt = typeof value === 'string'
                ? DateTime.fromISO(value, { zone: 'utc' })
                : DateTime.fromMillis(+value, { zone: 'utc' });
        } else {
            if (typeof value === 'string') {
                dt = DateTime.fromISO(value);
                if (!dt.isValid) {
                    dt = DateTime.fromSQL(value);
                }
                if (!dt.isValid) {
                    dt = DateTime.fromRFC2822(value);
                }
            } else if (value instanceof Date) {
                dt = DateTime.fromJSDate(value);
            } else if (typeof value === 'number') {
                dt = DateTime.fromMillis(value);
            } else {
                return value;
            }
        }

        if (!dt || !dt.isValid) {
            return value;
        }

        return dt;
    }

    public static formatDateForContractList(dateString: any) {
        const date = DateMoment.date(dateString);
        if (!date || !DateTime.isDateTime(date) || !date.isValid) {
            return dateString;
        }
        const now = DateTime.now();
        const today = now.startOf('day');
        const yesterday = today.minus({ days: 1 });
        const tomorrow = today.plus({ days: 1 });

        if (date.hasSame(today, 'day')) {
            return 'Today';
        } else if (date.hasSame(yesterday, 'day')) {
            return 'Yesterday';
        } else if (date.hasSame(tomorrow, 'day')) {
            return 'Tomorrow';
        }

        return date.toFormat('dd MMM yyyy');
    }

    public static fromNow(value: any) {
        if (!DateTime.isDateTime(value)) {
            value = DateMoment.date(value);
        }
        if (!DateTime.isDateTime(value)) return value;

        if (DateMoment.dateDiff(value, DateMoment.current(), true, 'days') < 2) {
            return value.toRelative();
        } else {
            return value.toFormat('dd-MM-yyyy HH:mm');
        }
    }

    public static toNow(value: any) {
        if (!DateTime.isDateTime(value)) {
            value = DateMoment.date(value);
        }
        if (!DateTime.isDateTime(value)) return value;

        return value.toRelative({ base: DateTime.now() });
    }

    public static getDatesBetween(startDate: any, endDate: any, unit: 'day' | 'month' | 'year' = 'day') {
        if (!DateTime.isDateTime(startDate)) startDate = DateMoment.date(startDate);
        if (!DateTime.isDateTime(endDate)) endDate = DateMoment.date(endDate);

        if (!DateTime.isDateTime(startDate) || !DateTime.isDateTime(endDate)) return [];

        startDate = startDate.startOf(unit);
        endDate = endDate.endOf(unit);

        const dates: DateTime[] = [];
        let current = startDate;
        while (current < endDate) {
            dates.push(current);
            current = current.plus({ [unit + 's']: 1 });
        }

        return dates;
    }

    public static countListDayOfMonths(startDate: any, endDate: any) {
        if (!DateTime.isDateTime(startDate)) startDate = DateMoment.date(startDate);
        if (!DateTime.isDateTime(endDate)) endDate = DateMoment.date(endDate);

        if (!DateTime.isDateTime(startDate) || !DateTime.isDateTime(endDate)) return [];

        const result: any[] = [];
        let start = (startDate as DateTime).startOf('day');
        const to = (endDate as DateTime);

        while (start <= to) {
            const endOfCurMonth = start.endOf('month');
            const end = endOfCurMonth <= to ? endOfCurMonth : to.endOf('day');

            result.push({
                period: `${start.toFormat('dd')} - ${end.toFormat('dd MMMM yyyy')}`,
                start: start,
                end: end,
                month: start.month - 1 // 0-based for backward compatibility
            });
            start = endOfCurMonth.plus({ milliseconds: 1 });
        }
        return result;
    }

    static isDateTime(val: any) {
        return DateTime.isDateTime(val);
    }

    /** @deprecated Use isDateTime instead */
    static isMoment(val: any) {
        return DateTime.isDateTime(val);
    }
}
